import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, FileStack, Check, Loader2, Building2, Users, Landmark, Printer } from "lucide-react";

import MouStepDocuments from "./MouStepDocuments.jsx";
import { DetailSkeleton } from "../../components/Skeleton.jsx";
import {
  getMouCase,
  updateMouCase,
  updateMouCaseStep,
  uploadMouCaseDocument,
  deleteMouCaseDocument,
  getMouCaseDocumentBlob,
} from "./mouCaseApi.js";
import {
  MOU_CASE_STATUS_OPTIONS,
  MOU_STEP_STATUS_OPTIONS,
  MOU_STEP_STATUS_DONE,
  caseStatusLabel,
  caseStatusVariant,
  describeMouError,
  progressPercent,
  stepStatusLabel,
  stepStatusVariant,
} from "./mouCaseShared.js";
import "./mouCase.css";

/**
 * P164: ONE ROUND's eight-step screen - the body of the rounds dialog, not a
 * dialog of its own.
 *
 * P164-R1 made it a panel. It used to be a second modal opened on top of the
 * drawer, which meant two stacked overlays and two Escape targets for one task.
 * Now MouRoundsDialog swaps its own body between the round grid and this panel,
 * so "open a round" is a move WITHIN one surface and the way back is a single
 * visible control rather than a dismissal.
 *
 * The step display follows the owner's own reference - "ให้มี step แสดงเหมือน
 * หน้าจอ working profile".
 *
 * Built to the same shape as the Detailed Worker Profile editor for that
 * reason, and it is the right shape for the same reasons that one gives: a
 * fixed identity band that never scrolls away (which company, which case, how
 * far along), a fixed strip of steps with a per-step count of what is still
 * missing, exactly ONE scroller between them, and a fixed footer. On a form
 * this long, letting the header and the step strip scroll off means an operator
 * loses both "what am I filing" and "how much is left" after the first field.
 *
 * Where it deliberately DIFFERS from that editor: the six tabs there are peer
 * sections of one record and can be read in any order, so they are a plain tab
 * strip. These eight are an ordered process a real filing moves through, so the
 * strip is a numbered stepper that shows what is done, what is current and what
 * is still ahead. Every step stays reachable at any time even so - a real case
 * goes backwards when an office returns a filing, and a screen that refused to
 * record that would push operators to record something else.
 *
 * Every write here is its own request applied immediately, and each one returns
 * the whole round, so the screen always renders what the server actually stored
 * rather than an optimistic guess that could disagree with it.
 */
export default function MouRoundStepsPanel({ plcOrganizationId, mouCaseId, titleId, onBack, onPrint, onChanged }) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const [activeStep, setActiveStep] = useState(1);
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [extraLabel, setExtraLabel] = useState("");
  const [stepDraft, setStepDraft] = useState(null);
  const stepRefs = useRef({});

  const detail = state.data;
  const step = detail?.steps?.find((candidate) => candidate.stepNumber === activeStep) ?? null;

  // --- load ---

  const load = useCallback(() => {
    if (!plcOrganizationId || !mouCaseId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));
    getMouCase(plcOrganizationId, mouCaseId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
          // Open on the step the case is actually at, not always at step 1 -
          // an operator coming back to a case at step 6 should not have to
          // find their place again.
          setActiveStep(data.currentStepNumber ?? 1);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [plcOrganizationId, mouCaseId]);

  useEffect(() => load(), [load]);

  // The step form is a draft so a half-typed reference number is not sent on
  // every keystroke; it is re-seeded whenever the step or the stored case
  // changes underneath it.
  useEffect(() => {
    if (!step) {
      setStepDraft(null);
      return;
    }
    setStepDraft({
      status: step.status ?? "",
      referenceNumber: step.referenceNumber ?? "",
      completedOn: step.completedOn ?? "",
      note: step.note ?? "",
    });
    setExtraLabel("");
  }, [step?.stepNumber, step?.status, step?.referenceNumber, step?.completedOn, step?.note]);

  // --- writes ---

  const run = useCallback(
    async (what, action) => {
      setPending(what);
      setActionError(null);
      try {
        const updated = await action();
        setState({ status: "ready", data: updated, error: null });
        onChanged?.(updated);
      } catch (caught) {
        setActionError(describeMouError(caught));
      } finally {
        setPending(null);
      }
    },
    [onChanged],
  );

  function saveStep() {
    if (!stepDraft || !step) {
      return;
    }
    run("step", () =>
      updateMouCaseStep(plcOrganizationId, mouCaseId, step.stepNumber, {
        status: stepDraft.status,
        referenceNumber: stepDraft.referenceNumber,
        completedOn: stepDraft.completedOn,
        note: stepDraft.note,
      }),
    );
  }

  function pickDocument(file, documentType) {
    run("document", () =>
      uploadMouCaseDocument(plcOrganizationId, mouCaseId, file, { stepNumber: activeStep, documentType }),
    );
    setExtraLabel("");
  }

  function removeDocument(document) {
    run("document", () => deleteMouCaseDocument(plcOrganizationId, mouCaseId, document.documentId));
  }

  /**
   * Opens one attached document in a new tab. The file route is authenticated,
   * so the bytes are fetched with the session token and handed to the browser as
   * an object URL - a plain href to the API path would carry no token and 401.
   */
  async function openDocument(document) {
    setActionError(null);
    try {
      const { blob } = await getMouCaseDocumentBlob(plcOrganizationId, mouCaseId, document.documentId);
      if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
        setActionError("เบราว์เซอร์นี้เปิดไฟล์ที่แนบไม่ได้");
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener");
      // Revoked on a delay rather than immediately: the new tab has to have
      // read the URL first, and revoking in the same tick leaves it blank.
      setTimeout(() => URL.revokeObjectURL?.(objectUrl), 60_000);
    } catch (caught) {
      setActionError(describeMouError(caught, "เปิดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"));
    }
  }

  function changeCaseStatus(status) {
    run("case", () => updateMouCase(plcOrganizationId, mouCaseId, { status }));
  }

  // --- keyboard navigation across the stepper, matching the profile editor ---

  function handleStepKeyDown(event) {
    const total = detail?.steps?.length ?? 0;
    if (!total) {
      return;
    }
    let next = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      next = (activeStep % total) + 1;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      next = activeStep === 1 ? total : activeStep - 1;
    } else if (event.key === "Home") {
      next = 1;
    } else if (event.key === "End") {
      next = total;
    }
    if (next === null) {
      return;
    }
    event.preventDefault();
    setActiveStep(next);
    stepRefs.current[next]?.focus();
  }

  /**
   * P164-R6: keep the selected step visible in the strip.
   *
   * The strip is one line now and scrolls sideways instead of wrapping, so on a
   * narrow window the tab that is actually open can sit off-screen - and a
   * round OPENS on whichever step it is really at, which on a phone was step 3
   * with only steps 1-2 in view. `inline: "center"` rather than "nearest"
   * deliberately: centring also reveals the steps on either side, which is the
   * context a stepper is read for.
   *
   * Optional-called because jsdom implements no scrollIntoView at all.
   */
  useEffect(() => {
    stepRefs.current[activeStep]?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [activeStep, detail?.mouCaseId]);

  const percent = useMemo(
    () => progressPercent(detail?.completedStepCount, detail?.stepCount),
    [detail?.completedStepCount, detail?.stepCount],
  );

  const busy = pending !== null;

  return (
    <>
        {state.status === "error" ? (
          <div className="wx-mou-state" role="alert">
            <h2 id={titleId}>ไม่สามารถโหลดเรื่องยื่น MOU ได้</h2>
            <p>{describeMouError(state.error, "กรุณาปิดหน้าต่างแล้วลองใหม่อีกครั้ง")}</p>
            <button type="button" className="wx-button wx-button--primary" onClick={onBack}>
              กลับไปหน้ารอบทั้งหมด
            </button>
          </div>
        ) : !detail ? (
          // The heading keeps titleId even while loading: the dialog's
          // own aria-labelledby points at it, so dropping it for a bare
          // skeleton would leave the dialog with no accessible name at exactly
          // the moment a screen-reader user arrives in it.
          <div className="wx-mou-state wx-mou-state--loading">
            <h2 id={titleId} className="wx-visually-hidden">
              กำลังโหลดเรื่องยื่น MOU...
            </h2>
            <DetailSkeleton label="กำลังโหลดเรื่องยื่น MOU..." sections={4} />
          </div>
        ) : (
          <>
            {/* Band 1 - which company, which case, how far along. Never scrolls. */}
            <header className="wx-mou-head">
              <p className="wx-mou-kicker">
                <FileStack size={16} aria-hidden="true" />
                การยื่น MOU {detail.countryLabel}
              </p>
              <div className="wx-mou-head-main">
                <div className="wx-mou-head-id">
                  <h2 id={titleId} className="wx-mou-title">
                    <span className="wx-mou-title-round">รอบที่ {detail.roundNumber}</span>
                    {detail.employerName || "ยังไม่ระบุนายจ้าง"}
                  </h2>
                  <p className="wx-mou-sub">
                    <span className="wx-mou-mono">{detail.caseCode}</span>
                    <span className="wx-mou-sub-sep" aria-hidden="true">·</span>
                    ยื่นโดย <strong>{detail.plcName}</strong>
                  </p>
                  <div className="wx-mou-pills">
                    <span className={`wx-badge wx-badge--${caseStatusVariant(detail.status)}`}>{caseStatusLabel(detail.status)}</span>
                    {detail.workerCountRequested != null && (
                      <span className="wx-mou-pill">
                        <Users size={13} aria-hidden="true" />
                        {detail.workerCountRequested} คน
                      </span>
                    )}
                    {detail.jobTypeLabel && (
                      <span className="wx-mou-pill">
                        <Building2 size={13} aria-hidden="true" />
                        {detail.jobTypeLabel}
                      </span>
                    )}
                    {detail.originCompanyName && (
                      <span className="wx-mou-pill">
                        <Landmark size={13} aria-hidden="true" />
                        {detail.originCompanyName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="wx-mou-head-aside">
                  <div className={`wx-mou-meter ${percent === 100 ? "wx-mou-meter--complete" : ""}`}>
                    <div className="wx-mou-meter-head">
                      <span className="wx-mou-meter-caption">ความคืบหน้า</span>
                      <strong className="wx-mou-meter-value">{percent}%</strong>
                    </div>
                    <div
                      className="wx-mou-meter-track"
                      role="progressbar"
                      aria-valuenow={detail.completedStepCount}
                      aria-valuemin={0}
                      aria-valuemax={detail.stepCount}
                      aria-label="ขั้นตอนที่เสร็จแล้ว"
                    >
                      <div
                        className={`wx-mou-meter-fill ${percent === 100 ? "wx-mou-meter-fill--complete" : ""}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="wx-mou-meter-caption">
                      เสร็จแล้ว {detail.completedStepCount} จาก {detail.stepCount} ขั้นตอน · ยังขาดเอกสาร {detail.missingDocumentCount} รายการ
                    </span>
                  </div>
                  {/* P164-R5: the same forms the grid card offers, reachable
                      from inside the round too - this is where an operator
                      actually is when the paperwork is ready to go out. */}
                  <button
                    type="button"
                    className="wx-mou-close"
                    onClick={onPrint}
                    aria-label="พิมพ์แบบฟอร์มสำหรับยื่นหน่วยงานราชการ"
                    title="พิมพ์แบบฟอร์มสำหรับยื่นหน่วยงานราชการ"
                  >
                    <Printer size={17} aria-hidden="true" />
                  </button>
                  <button type="button" className="wx-mou-close" onClick={onBack} aria-label="กลับไปหน้ารอบทั้งหมด">
                    <ArrowLeft size={17} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </header>

            <div className="wx-mou-body">
              {/* Band 2 - the eight steps. A numbered stepper, not a plain tab
                  strip: these are an ordered process, and which ones are behind
                  you is half the answer an operator opens this screen for. */}
              <div className="wx-mou-steps">
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
                <nav className="wx-mou-stepper" aria-label="ขั้นตอนการยื่น MOU" role="tablist" onKeyDown={handleStepKeyDown}>
                  {detail.steps.map((candidate) => {
                    const active = candidate.stepNumber === activeStep;
                    const done = candidate.status === MOU_STEP_STATUS_DONE;
                    return (
                      <button
                        key={candidate.stepNumber}
                        type="button"
                        role="tab"
                        id={`wx-mou-step-tab-${candidate.stepNumber}`}
                        ref={(node) => {
                          stepRefs.current[candidate.stepNumber] = node;
                        }}
                        className={`wx-mou-step ${active ? "wx-mou-step--active" : ""} ${done ? "wx-mou-step--done" : ""}`}
                        aria-selected={active}
                        aria-controls={`wx-mou-step-panel-${candidate.stepNumber}`}
                        tabIndex={active ? 0 : -1}
                        onClick={() => setActiveStep(candidate.stepNumber)}
                      >
                        <span className="wx-mou-step-mark" aria-hidden="true">
                          {done ? <Check size={13} /> : candidate.stepNumber}
                        </span>
                        {/* P164-R6: the SHORT title on the tab. Owner: "tab
                            ชื่อให้กระชับไม่ต้องเจาะจงประเทศ เพื่อให้มันอยู่บรรทัด
                            เดียวกัน". It carries no country name - the round's
                            own header already says which country this is, and
                            repeating it on all eight tabs was what pushed the
                            strip onto a second line. The full title still heads
                            the step's panel below. Falls back to the full title
                            so a server that has not been redeployed yet renders
                            something real rather than an empty tab. */}
                        <span className="wx-mou-step-label">{candidate.shortTitle || candidate.title}</span>
                      </button>
                    );
                  })}
                </nav>
                {/* P164-R6 removed the per-tab missing-document count. Owner:
                    "ตัวเลขหลัง tab เอาออกครับ งง" - and they were right, the
                    same number was already on the round's own progress line and
                    again inside each step's checklist, so a third copy on the
                    tab could only ever raise the question of which one to read.
                    The screen-reader-only announcement of that count went with
                    it, on purpose: leaving it would tell a screen reader a
                    number no sighted operator can see. It is still read out in
                    the step's own panel, where the sighted one reads it too. */}
              </div>

              {/* Band 3 - the one scroller. */}
              <div className="wx-mou-scroll">
                {step && stepDraft && (
                  <section
                    className="wx-mou-panel"
                    id={`wx-mou-step-panel-${step.stepNumber}`}
                    role="tabpanel"
                    aria-labelledby={`wx-mou-step-tab-${step.stepNumber}`}
                  >
                    <div className="wx-mou-panel-head">
                      <div>
                        <p className="wx-mou-panel-kicker">
                          ขั้นตอนที่ {step.stepNumber} จาก {detail.stepCount}
                        </p>
                        <h3 className="wx-mou-panel-title">{step.title}</h3>
                        <p className="wx-mou-panel-summary">{step.summary}</p>
                      </div>
                      <span className={`wx-badge wx-badge--${stepStatusVariant(step.status)}`}>{stepStatusLabel(step.status)}</span>
                    </div>

                    <div className="wx-mou-fields">
                      <label className="wx-mou-field">
                        <span>สถานะขั้นตอน</span>
                        <select
                          aria-label="สถานะขั้นตอน"
                          value={stepDraft.status}
                          disabled={busy}
                          onChange={(event) => setStepDraft((previous) => ({ ...previous, status: event.target.value }))}
                        >
                          {MOU_STEP_STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="wx-mou-field">
                        <span>เลขที่อ้างอิง / เลขที่รับเรื่อง</span>
                        <input
                          type="text"
                          aria-label="เลขที่อ้างอิง"
                          maxLength={120}
                          placeholder="เช่น OS-2569-0042"
                          value={stepDraft.referenceNumber}
                          disabled={busy}
                          onChange={(event) => setStepDraft((previous) => ({ ...previous, referenceNumber: event.target.value }))}
                        />
                      </label>

                      <label className="wx-mou-field">
                        <span>วันที่ดำเนินการเสร็จ</span>
                        <input
                          type="date"
                          aria-label="วันที่ดำเนินการเสร็จ"
                          value={stepDraft.completedOn}
                          disabled={busy}
                          onChange={(event) => setStepDraft((previous) => ({ ...previous, completedOn: event.target.value }))}
                        />
                      </label>

                      <label className="wx-mou-field wx-mou-field--wide">
                        <span>บันทึกของขั้นตอนนี้</span>
                        <textarea
                          aria-label="บันทึกของขั้นตอนนี้"
                          rows={2}
                          maxLength={1000}
                          placeholder="เช่น เจ้าหน้าที่ขอเอกสารเพิ่ม หรือ นัดฟังผลวันที่..."
                          value={stepDraft.note}
                          disabled={busy}
                          onChange={(event) => setStepDraft((previous) => ({ ...previous, note: event.target.value }))}
                        />
                      </label>
                    </div>

                    <div className="wx-mou-panel-actions">
                      <button type="button" className="wx-button wx-button--primary" disabled={busy} onClick={saveStep}>
                        {pending === "step" ? "กำลังบันทึก..." : "บันทึกขั้นตอนนี้"}
                      </button>
                    </div>

                    <MouStepDocuments
                      requiredDocuments={step.requiredDocuments ?? []}
                      documents={step.documents ?? []}
                      disabled={busy}
                      extraLabel={extraLabel}
                      onExtraLabelChange={setExtraLabel}
                      onPick={pickDocument}
                      onRemove={removeDocument}
                      onOpen={openDocument}
                    />
                  </section>
                )}
              </div>

              {/* Band 4 - the case as a whole, and the way out. */}
              <footer className="wx-mou-foot">
                <label className="wx-mou-foot-field">
                  <span>สถานะของเรื่อง</span>
                  <select
                    aria-label="สถานะของเรื่อง"
                    value={detail.status}
                    disabled={busy}
                    onChange={(event) => changeCaseStatus(event.target.value)}
                  >
                    {MOU_CASE_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="wx-mou-foot-status" role="status">
                  {busy && (
                    <span className="wx-mou-busy">
                      <Loader2 size={13} aria-hidden="true" />
                      กำลังบันทึก...
                    </span>
                  )}
                </div>

              </footer>
            </div>

            {actionError && (
              <p className="wx-mou-error" role="alert">
                {actionError}
              </p>
            )}
          </>
        )}
    </>
  );
}